/**
 * PendencyList
 * Pendências devolvidas pela prévia, separadas visualmente pelo que fazem:
 * o que impede a emissão vem primeiro, em vermelho; o que apenas avisa vem
 * depois. Sem essa separação, quem emite não sabe o que precisa resolver agora.
 *
 * Quando a pendência traz `action`, a lista mostra o CAMINHO da solução — sem
 * ele, avisos como "não tem ART válida cadastrada" são beco sem saída: quem
 * descobre o problema está aqui, e a tela que o resolve é de outra entidade.
 *
 * O link só aparece para quem tem a permissão que a API do destino exige. Sem
 * ela, sai o recado de pedir a um administrador — botão que responderia 403 é
 * pior do que botão nenhum.
 *
 * @version 2026.1.0
 */

import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAcao } from '../../../authorization/useVeredito';
import type { Pendency } from '../../../types/document';

interface PendencyListProps {
  pendencies: Pendency[];
}

/**
 * Item isolado em componente porque é ele que consulta a permissão — e a
 * consulta é por pendência, já que cada ação declara a sua.
 */
const PendencyItem: React.FC<{ pendencia: Pendency }> = ({ pendencia }) => {
  const { action } = pendencia;
  const resolver = useAcao(action?.requiredPermission ?? '', action?.label ?? '');

  return (
    <ListItem disablePadding>
      <ListItemText
        primary={pendencia.message}
        secondary={
          <>
            {pendencia.sectionKey !== undefined && pendencia.sectionKey !== '' && (
              <Box component="span" sx={{ display: 'block' }}>
                {`Seção: ${pendencia.sectionKey}`}
              </Box>
            )}
            {action !== undefined && !resolver.disabled && (
              <Link component={RouterLink} to={action.route} underline="always">
                {action.label}
              </Link>
            )}
            {action !== undefined && resolver.disabled && (
              <Box component="span">{`${resolver.tooltip} — peça a um administrador.`}</Box>
            )}
          </>
        }
        slotProps={{ secondary: { component: 'div' } }}
      />
    </ListItem>
  );
};

const renderGrupo = (
  titulo: string,
  itens: Pendency[],
  severidade: 'error' | 'warning'
): React.ReactNode => (
  <Alert severity={severidade} sx={{ mb: 2 }}>
    <AlertTitle>{titulo}</AlertTitle>
    <List dense disablePadding>
      {itens.map((pendencia) => (
        <PendencyItem
          key={`${pendencia.code}-${pendencia.sectionKey ?? ''}-${pendencia.message}`}
          pendencia={pendencia}
        />
      ))}
    </List>
  </Alert>
);

export const PendencyList: React.FC<PendencyListProps> = ({ pendencies }) => {
  const bloqueantes = pendencies.filter((pendencia) => pendencia.severity === 'bloqueia');
  const avisos = pendencies.filter((pendencia) => pendencia.severity === 'avisa');

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Pendências
      </Typography>

      {pendencies.length === 0 && (
        <Alert severity="success">Nenhuma pendência encontrada para esta emissão.</Alert>
      )}

      {bloqueantes.length > 0 &&
        renderGrupo('Impedem a emissão — resolva antes de gerar', bloqueantes, 'error')}

      {avisos.length > 0 && renderGrupo('Avisos — não impedem a emissão', avisos, 'warning')}
    </Box>
  );
};

export default PendencyList;
